import { ref, watch, useNuxtApp } from '#imports';
import { validators as regexpValidators } from '~dsc/utils/form';

export const useFormData = (formData, initOptions = {}) => {
    const { $i18n } = useNuxtApp();
    const errors = ref({});
    const data = ref({});
    const initialValues = {};
    const validState = ref({});

    const options = Object.assign({
        behavior: 'delay',
        delay: 500,
    }, initOptions);

    if (!Array.isArray(formData)) {
        throw new Error('useFormData expects an array of form field configurations');
    }

    if (Array.isArray(formData)) {
        formData.forEach(item => {
            if (!item || typeof item !== 'object' || !item.key) {
                throw new Error('Each form field must be an object with a key property');
            }
            data.value[item.key] = Array.isArray(item.value) ? [...item.value] : item.value;
            initialValues[item.key] = Array.isArray(item.value) ? [...item.value] : item.value;

            if (item.validators) {
                validState.value[item.key] = false;
            }
        });
    }

    const reset = () => {
        Object.keys(data.value).forEach(key => {
            if (initialValues[key]) {
                if (Array.isArray(initialValues[key])) {
                    data.value[key] = [...initialValues[key]];
                } else {
                    data.value[key] = initialValues[key];
                }
            } else {
                data.value[key] = null;
            }
        });

        formData.forEach(item => {
            if (item.validators) {
                validState.value[item.key] = false;
            }
        });

        errors.value = {};
    };

    const validate = (targets = null) => {
        if (!targets || !Array.isArray(targets)) return;

        targets.forEach(key => {
            const { validators } = formData.find(item => item.key === key) || {};

            if (validators) {
                try {
                    validators.forEach(validator => {
                        let validationResult;

                        switch (typeof(validator)) {
                            case 'object':
                                validationResult = regexpValidators[validator.type](data.value[key], validator.rule);
                            break;

                            default:
                            case 'string':
                                validationResult = regexpValidators[validator](data.value[key]);
                            break;
                        }

                        if (typeof(validationResult) === 'string') throw { msg: validationResult, validator }; // eslint-disable-line no-throw-literal
                    });
                    validState.value[key] = true;
                    setTimeout(() => {
                        delete errors.value[key];
                    }, options.behavior === 'delay' ? options.delay : 0);
                } catch (e) {
                    validState.value[key] = false;

                    setTimeout(() => {
                        errors.value[key] = $i18n.t(e.msg, { ruleValue: e.validator?.rule?.value || null });
                    }, options.behavior === 'delay' ? options.delay : 0);
                }
            }
        });
    };

    const isValid = (target = null) => validState.value[target] ?? true;

    watch(() => JSON.stringify(data.value), () => {
        Object.keys(data.value).forEach(key => {
            if (Array.isArray(data.value[key])) {
                // validate array
            } else if (data.value[key] !== initialValues[key]) {
                validate([key]);
            }
        });
    });

    return {
        options,
        errors,
        data,
        validState,
        isValid,
        validate,
        reset,
    };
};
